import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Mail, ArrowLeft, CheckCircle2 } from "lucide-react";
import { authApi } from "@/lib/api";
import { getApiError } from "@/lib/utils";

/** Public page — never reveals whether an email is registered. */
export default function ForgotPassword() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email.trim()) {
      toast.error("Enter your email");
      return;
    }
    setSubmitting(true);
    try {
      await authApi.forgotPassword(email.trim().toLowerCase());
      setDone(true);
    } catch (err) {
      // Backend always returns ok — but network errors can surface here
      toast.error(getApiError(err, "Something went wrong. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-slate-50 p-6" data-testid="forgot-password-page">
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-8 shadow-sm">
        <button
          onClick={() => nav("/login")}
          className="mb-6 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-800"
        >
          <ArrowLeft size={13} /> Back to sign in
        </button>

        {done ? (
          <div className="text-center" data-testid="forgot-password-sent">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100">
              <CheckCircle2 size={28} className="text-emerald-700" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">Check your email</h1>
            <p className="mt-2 text-sm text-slate-600">
              If <b>{email}</b> matches an account, we've sent a one-time reset link. It
              expires in <b>15 minutes</b>.
            </p>
            <p className="mt-4 text-xs text-slate-500">
              Didn't get it? Check spam, or wait a couple of minutes before trying again.
            </p>
            <button
              onClick={() => nav("/login")}
              className="mt-6 inline-flex w-full items-center justify-center rounded-md bg-nmi-700 px-4 py-2.5 text-sm font-bold text-white hover:bg-nmi-800"
              data-testid="forgot-password-back-to-login"
            >
              Return to sign in
            </button>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.2em] text-nmi-700">
                Password reset
              </div>
              <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">
                Forgot your password?
              </h1>
              <p className="mt-2 text-sm text-slate-600">
                Enter your work email and we'll send you a one-time reset link. It expires in <b>15 minutes</b>.
              </p>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">
                  Email
                </label>
                <div className="relative">
                  <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="email"
                    data-testid="forgot-password-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="username"
                    required
                    placeholder="you@nationwidemh.com"
                    className="w-full rounded-md border border-slate-200 bg-white pl-9 pr-3 py-2.5 text-sm outline-none focus:border-nmi-600 focus:ring-1 focus:ring-nmi-600"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="forgot-password-submit"
                className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-nmi-700 px-4 py-2.5 text-sm font-bold text-white hover:bg-nmi-800 disabled:opacity-60"
              >
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
                Send reset link
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
